/**
 * Converter client for drag-drop convert-first flow (handover gate item 10).
 *
 * Upload → run vector-pmtiles / raster-cog-pmtiles → poll → return browser-reachable URL
 * plus geographic metadata for G23/G25-conformant pin payloads.
 */

import { normalizeCrsCode } from '@/crs/crsCatalog';

const CONVERTER_BASE =
  (import.meta.env.VITE_CONVERTER_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ||
  'http://127.0.0.1:3011';

export interface ConversionArtifact {
  readonly pmtilesUrl: string;
  readonly sourceCrs: string;
  readonly bbox: [number, number, number, number];
  readonly title: string;
}

export interface RasterStagingArtifact {
  readonly cogUrl: string;
  readonly sourceCrs: string;
  readonly bbox: [number, number, number, number];
  readonly title: string;
}

interface UploadResult {
  readonly path: string;
  readonly size: number;
  readonly name: string;
}

interface ConversionRunRecord {
  readonly runId: string;
  readonly status: 'running' | 'succeeded' | 'failed' | string;
  readonly artifacts: string[];
  readonly error?: string | null;
}

interface SourceProfile {
  readonly crs?: string | null;
  readonly bbox?: number[] | null;
}

function makeDropSubdir(): string {
  return `drop-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Map a container output path to a browser-reachable `/converter-output/...` URL. */
export function converterOutputUrl(containerPath: string): string {
  const normalized = containerPath.replace(/\\/g, '/');
  const markers = ['/data/out/', '/app/data/out/'];
  for (const marker of markers) {
    const idx = normalized.toLowerCase().indexOf(marker.toLowerCase());
    if (idx >= 0) {
      const rel = normalized.slice(idx + marker.length);
      const encoded = rel
        .split('/')
        .filter(Boolean)
        .map((segment) => encodeURIComponent(segment))
        .join('/');
      return `${CONVERTER_BASE}/converter-output/${encoded}`;
    }
  }
  const fileName = normalized.split('/').pop() ?? normalized;
  return `${CONVERTER_BASE}/converter-output/${encodeURIComponent(fileName)}`;
}

function profileUrlForStem(stem: string): string {
  return `${CONVERTER_BASE}/converter-output/${encodeURIComponent(`${stem}.source-profile.json`)}`;
}

async function readJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function uploadFileToConverter(
  file: File,
  subdir: string,
  overwrite = false,
): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('subdir', subdir);
  if (overwrite) form.append('overwrite', 'true');
  const res = await fetch(`${CONVERTER_BASE}/api/v1/converter/upload`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Converter upload failed (${res.status}): ${detail.slice(0, 240)}`);
  }
  return (await res.json()) as UploadResult;
}

async function uploadFilesToConverter(
  files: readonly File[],
  subdir: string,
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  for (const file of files) {
    results.push(await uploadFileToConverter(file, subdir));
  }
  return results;
}

async function startConversion(
  inputPath: string,
  converterId: string,
): Promise<ConversionRunRecord> {
  const res = await fetch(`${CONVERTER_BASE}/api/v1/converter/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inputPath, converterId }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Converter run failed (${res.status}): ${detail.slice(0, 240)}`);
  }
  return (await res.json()) as ConversionRunRecord;
}

async function pollRun(runId: string, timeoutMs = 120_000): Promise<ConversionRunRecord> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const res = await fetch(`${CONVERTER_BASE}/api/v1/converter/runs/${encodeURIComponent(runId)}`);
    if (!res.ok) {
      throw new Error(`Converter run poll failed (${res.status})`);
    }
    const rec = (await res.json()) as ConversionRunRecord;
    if (rec.status === 'succeeded') return rec;
    if (rec.status === 'failed') {
      throw new Error(rec.error ?? 'Conversion failed');
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  throw new Error(`Conversion timed out after ${timeoutMs / 1000}s`);
}

function pickPmtilesPath(artifacts: readonly string[]): string | null {
  const match = artifacts.find((path) => path.replace(/\\/g, '/').endsWith('.pmtiles'));
  return match ?? null;
}

function pickCogPath(artifacts: readonly string[]): string | null {
  const match = artifacts.find((path) => {
    const norm = path.replace(/\\/g, '/').toLowerCase();
    return norm.endsWith('.cog.tif') || norm.endsWith('.cog.tiff');
  });
  return match ?? null;
}

function normalizeGeographicBbox(raw: unknown): [number, number, number, number] | null {
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const west = Number(raw[0]);
  const south = Number(raw[1]);
  const east = Number(raw[2]);
  const north = Number(raw[3]);
  if (![west, south, east, north].every(Number.isFinite)) return null;
  if (west < -180 || east > 180 || south < -90 || north > 90) return null;
  if (east <= west || north <= south) return null;
  return [west, south, east, north];
}

function resolveGeography(
  _file: File,
  metadata: { readonly crs?: string; readonly bbox?: readonly [number, number, number, number] },
  profile: SourceProfile | null,
): { sourceCrs: string; bbox: [number, number, number, number] } {
  const sourceCrs = metadata.crs
    ? normalizeCrsCode(metadata.crs)
    : profile?.crs
      ? normalizeCrsCode(profile.crs)
      : 'EPSG:4326';

  const bbox =
    normalizeGeographicBbox(metadata.bbox) ??
    normalizeGeographicBbox(profile?.bbox) ??
    null;

  if (!bbox) {
    throw new Error(
      'Conversion succeeded but geographic bbox is missing — pin would fail G23 validation',
    );
  }

  return { sourceCrs, bbox };
}

/**
 * Upload a local vector file (and optional shapefile sidecars), convert to PMTiles,
 * and return a renderable URL with geographic CRS + bbox suitable for scene-item pin.
 */
export async function convertVectorFileToPmtiles(
  file: File,
  metadata: { readonly crs?: string; readonly bbox?: readonly [number, number, number, number] },
  sidecars: readonly File[] = [],
): Promise<ConversionArtifact> {
  const subdir = makeDropSubdir();
  const uploads = await uploadFilesToConverter([file, ...sidecars], subdir);
  const primary =
    uploads.find((u) => u.name.toLowerCase().endsWith('.shp')) ??
    uploads.find((u) => u.name === file.name) ??
    uploads[0];
  if (!primary) {
    throw new Error('Converter upload produced no files');
  }

  const started = await startConversion(primary.path, 'vector-pmtiles');
  const finished = await pollRun(started.runId);

  const pmtilesPath = pickPmtilesPath(finished.artifacts);
  if (!pmtilesPath) {
    throw new Error('Converter succeeded but produced no PMTiles artifact');
  }

  const stem = file.name.replace(/\.[^.]+$/, '');
  const profile = await readJson<SourceProfile>(profileUrlForStem(stem));
  const { sourceCrs, bbox } = resolveGeography(file, metadata, profile);

  return {
    pmtilesUrl: converterOutputUrl(pmtilesPath),
    sourceCrs,
    bbox,
    title: file.name,
  };
}

/**
 * Upload a local GeoTIFF/COG, run raster-cog-pmtiles, and return a browser-reachable COG URL.
 */
export async function stageLocalRasterForPin(
  file: File,
  metadata: { readonly crs?: string; readonly bbox?: readonly [number, number, number, number] },
): Promise<RasterStagingArtifact> {
  const subdir = makeDropSubdir();
  const upload = await uploadFileToConverter(file, subdir);
  const started = await startConversion(upload.path, 'raster-cog-pmtiles');
  const finished = await pollRun(started.runId, 180_000);

  const cogPath = pickCogPath(finished.artifacts);
  if (!cogPath) {
    throw new Error('Raster conversion succeeded but produced no COG artifact');
  }

  const stem = file.name.replace(/\.[^.]+$/, '');
  const profile = await readJson<SourceProfile>(profileUrlForStem(stem));
  const { sourceCrs, bbox } = resolveGeography(file, metadata, profile);

  return {
    cogUrl: converterOutputUrl(cogPath),
    sourceCrs,
    bbox,
    title: file.name,
  };
}
